// src/QuoteFormNav.js — Mini-sommaire flottant pour QuoteForm
import React, { useState, useEffect, useRef } from 'react';

const SECTIONS = [
  { id: 'section-articles',  label: 'Articles',  icon: '📦' },
  { id: 'section-boutons',   label: 'Kits',      icon: '\u26a1' },
  { id: 'section-options',   label: 'Options',   icon: '\u2699' },
  { id: 'section-total',     label: 'Total',     icon: '\u{1F4B0}' },
];

const QuoteFormNav = ({ sections = SECTIONS }) => {
  const [visible, setVisible] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const isClicking = useRef(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 200);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observers = [];
    sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !isClicking.current) {
            setActiveId(s.id);
          }
        },
        { threshold: 0.1, rootMargin: '0px 0px -50% 0px' }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, [sections]);

  const scrollTo = (id) => {
    isClicking.current = true;
    setActiveId(id);
    setExpanded(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => { isClicking.current = false; }, 1500);
  };

  const scrollTop = () => {
    isClicking.current = true;
    setActiveId(null);
    setExpanded(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => { isClicking.current = false; }, 1500);
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', bottom: '28px', right: '28px',
      zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px',
    }}>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {expanded && (
        <div style={{
          background: 'white', borderRadius: '14px', padding: '10px 8px',
          boxShadow: '0 8px 32px rgba(30,64,175,0.18)',
          border: '1px solid #e5e7eb',
          animation: 'fadeIn 0.2s ease',
          minWidth: '160px',
        }}>
          <button onClick={scrollTop}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', border: 'none', background: '#eff6ff', borderRadius: '8px', cursor: 'pointer', marginBottom: '6px' }}>
            <span style={{ fontSize: '14px' }}>⬆️</span>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e40af' }}>Haut du formulaire</span>
          </button>

          <div style={{ fontSize: '10px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 10px 6px' }}>
            Sections
          </div>

          {sections.map(s => (
            <button key={s.id} onClick={() => scrollTo(s.id)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
                padding: '7px 10px', border: 'none',
                background: activeId === s.id ? '#eff6ff' : 'transparent',
                borderRadius: '8px', cursor: 'pointer', marginBottom: '2px',
                borderLeft: activeId === s.id ? '3px solid #1e40af' : '3px solid transparent',
                transition: 'all 0.15s',
              }}>
              <span style={{ fontSize: '14px' }}>{s.icon}</span>
              <span style={{ fontSize: '13px', fontWeight: activeId === s.id ? 700 : 400, color: activeId === s.id ? '#1e40af' : '#374151' }}>
                {s.label}
              </span>
              {activeId === s.id && (
                <span style={{ marginLeft: 'auto', width: '6px', height: '6px', borderRadius: '50%', background: '#1e40af' }} />
              )}
            </button>
          ))}
        </div>
      )}

      <button
        onClick={() => setExpanded(v => !v)}
        title={expanded ? 'Fermer' : 'Navigation rapide'}
        style={{
          width: '52px', height: '52px', borderRadius: '50%',
          background: expanded ? '#1e3a8a' : 'linear-gradient(135deg, #1e40af, #3b82f6)',
          border: 'none', cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(30,64,175,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s ease',
          animation: 'fadeSlideIn 0.25s ease',
        }}>
        {expanded ? (
          <svg width="16" height="16" viewBox="0 0 16 16">
            <line x1="2" y1="2" x2="14" y2="14" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
            <line x1="14" y1="2" x2="2" y2="14" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
          </svg>
        ) : (
          <svg width="18" height="14" viewBox="0 0 18 14">
            <line x1="1" y1="2" x2="17" y2="2" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            <line x1="1" y1="7" x2="13" y2="7" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            <line x1="1" y1="12" x2="17" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )}
      </button>
    </div>
  );
};

export default QuoteFormNav;
